import React, { useCallback, useState, useEffect } from "react";
import SidebarCollapse from "@builder/components/app/SidebarCollapse";
import Input from "@builder/components/general/Input";
import { updateNodeProperty } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { EditableProps } from "@builder/components/editable/editableProps";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { CustomNumpadNode } from "@common/build/types";
import "./style.scss";

// Simple number-only input for grid columns
const NumpadGrid: React.FC<EditableProps> = ({ title, parent, noWrapper }) => {
    const dispatch = useAppDispatch();
    const numpadNode = useAppSelector(getSelectedNode) as CustomNumpadNode;

    const [inputValue, setInputValue] = useState((numpadNode?.gridConfig?.columns || 3).toString());

    useEffect(() => {
        const columns = numpadNode?.gridConfig?.columns || 3;
        setInputValue(columns.toString());
    }, [numpadNode?.gridConfig?.columns]);

    const handleColumnChange = useCallback(
        (value: string) => {
            setInputValue(value);

            if (value !== "" && !isNaN(parseInt(value))) {
                const numValue = parseInt(value);
                if (numValue > 0) {
                    dispatch(
                        updateNodeProperty({
                            propertyPath: ["gridConfig", "columns"],
                            value: numValue
                        })
                    );
                }
            } else if (value === "") {
                // allows for deletion while typing
            }
        },
        [dispatch]
    );

    // When input loses focus, restore to valid value if empty
    const handleClick = () => {
        setTimeout(() => {
            if (document.activeElement !== document.querySelector(".numpad-grid-container input")) {
                if (inputValue === "" || isNaN(parseInt(inputValue)) || parseInt(inputValue) <= 0) {
                    // Reset to default if invalid when focus is lost
                    setInputValue("3");
                    dispatch(
                        updateNodeProperty({
                            propertyPath: ["gridConfig", "columns"],
                            value: 3
                        })
                    );
                }
            }
        }, 0);
    };

    // Add global click handler to detect focus loss
    useEffect(() => {
        document.addEventListener("click", handleClick);
        return () => {
            document.removeEventListener("click", handleClick);
        };
    }, [inputValue]);

    const content = (
        <div className="numpad-grid-container">
            <Input label="Columns per row" direction="row" value={inputValue} placeholder="3" callback={handleColumnChange} filter={/^\d*$/} />
        </div>
    );

    if (noWrapper) {
        return content;
    }

    return (
        <SidebarCollapse title={title || "Numpad Grid Layout"} parent={parent}>
            {content}
        </SidebarCollapse>
    );
};

export default NumpadGrid;
